<template>
  <div>
    <h2 class="mb-4">Testamentary Trust</h2>

    <h3>What is a Testamentary Trust?</h3>
    <br />
    <p>
      A Testamentary Trust is a way to ensure that your assets are managed and
      protected for your children's benefit after you're gone. It involves
      creating a separate legal entity, managed by Trustees you choose, who will
      oversee these assets according to your wishes.
      <br />
      <a
        v-if="whitelabel.showTrustLearnMoreLink"
        href="http://support.quickwill.co.za/en/articles/3111496-what-are-the-differences-between-a-children-s-trust-and-the-guardian-s-fund"
        >Learn more</a
      >
    </p>

    <h3>What is the role of a Trustee?</h3>
    <br />
    <ul>
      <li>
        A Trustee's responsibility is to manage the assets of the trust to the
        best of their ability for the benefit of the beneficiaries.
      </li>
      <li>
        Trustees can earn a market related fee and can outsource work they deem
        necessary.
      </li>
      <li>
        We recommend you appoint more than one Trustee (up to a total of three).
      </li>
    </ul>
    <br />

    <h3>Should I use a trust?</h3>
    <br />
    <ul>
      <li>
        If you don't set up a Testamentary Trust in your will and your child is
        under 18, their assets will go to the Government’s Guardians Fund to be
        managed. This can lead to challenges and delays in getting those assets
        to your child.
      </li>
      <li>
        If your children are over 18, or once they reach that age, you might
        prefer a trust where Trustees manage their assets until they are mature
        enough to take full responsibility.
      </li>
    </ul>
    <br />
    <ValidationObserver ref="observer">
      <h3>Do you want to create a Testamentary Trust?</h3>
      <RadioSelect
        v-model="createTrust"
        :items="createTrustOptions"
        :group="'createTrust'"
        @change="testamentaryTrustSelected"
      />

      <div v-if="createTrust">
        <h3 class="mb-6">
          Age of my youngest child, when the trust will terminate:
        </h3>
        <ValidationProvider
          id="trust_termination"
          v-slot="{ errors }"
          name="trust_termination"
          rules="integer|min_value:18"
        >
          <TextField
            v-model="trustTerminationAge"
            :suffix="'years'"
            :errors="errors"
            :name="'trust_termination'"
          />
        </ValidationProvider>
      </div>
    </ValidationObserver>

    <div v-if="createTrust && trustTerminationAge">
      <h3>Trustee(s):</h3>
      <PeopleList
        :people="relations.trustees"
        :testator="testator"
        relation="TRUSTEE"
        :limit="3"
        :people-for-dialog="peopleForDialog"
      />
    </div>

    <div class="button-container">
      <DefaultButton
        @defaultButtonClicked="redirectBack"
        :callout="'left'"
        :small="true"
      />
      <GradientButton
        v-if="displayNextButton"
        :title="'Next'"
        :small="true"
        :callout="'right'"
        :loading="buttonLoading"
        @gradientButtonClicked="saveAndNext"
      />
    </div>
    <TestamentaryTrustDialog
      v-model="showTestamentaryTrustDialog"
      @dontDeleteTestamentaryTrust="dontDeleteTestamentaryTrust"
    />
    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import { UPDATE_TESTATOR_MUTATION } from '../../constants/graphql/testators';
  import PeopleList from './PeopleList.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import TestamentaryTrustDialog from '../people/TestamentaryTrustDialog.vue';

  export default {
    name: 'ChildrensTrust',
    components: {
      PeopleList,
      ErrorSnackbar,
      TestamentaryTrustDialog,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        required: true,
        type: Object,
      },
      relations: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        createTrust: this.testator.createChildrensTrust,
        trustTerminationAge: this.testator.trustTerminationAge,
        wizard: this.$route.query.peopleWizard,
        showTestamentaryTrustDialog: false,
        createTrustOptions: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
      };
    },
    computed: {
      testamentaryTrust() {
        return this.testator.trusts.filter(
          t => t.testamentaryTrust === 'true'
        )[0];
      },
      hasTestamentaryTrustBequests() {
        return (
          this.testamentaryTrust &&
          !!this.testamentaryTrust?.specificDonations?.length
        );
      },
      displayNextButton() {
        if (this.createTrust) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'your-people-trust-yes',
            omuserid: this.$store.getters?.userId,
          });
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'your-people-trust-no',
            omuserid: this.$store.getters?.userId,
          });
        }

        return (
          this.createTrust == false ||
          (this.createTrust &&
            this.trustTerminationAge &&
            (this.relations.trustees.length > 0 ||
              this.testator.companyIsTrustee))
        );
      },
      peopleForDialog() {
        return this.testator.people.map(p => {
          if (this.relations.trustees.includes(p)) {
            return {
              ...p,
              disabledMessage: 'This person is already a Trustee',
            };
          } else if (this.relations.children.includes(p)) {
            return {
              ...p,
              disabledMessage:
                'Children cannot be Trustees of their own trusts',
            };
          } else {
            return p;
          }
        });
      },
    },
    created() {
      if (this.testator.willCountry == 'GB') {
        this.redirectToNext();
      }
    },
    methods: {
      testamentaryTrustSelected(value) {
        if (value === 'on' && this.hasTestamentaryTrustBequests !== undefined) {
          this.showTestamentaryTrustDialog = true;
        }
      },
      dontDeleteTestamentaryTrust() {
        this.createTrust = true;
      },
      saveAndNext() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateTestator();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      updateTestator() {
        const testatorInputDetails = {
          createChildrensTrust: this.createTrust,
          trusteeParty: this.trusteeParty,
          trustTerminationAge: parseInt(this.trustTerminationAge),
        };
        const id = this.testator.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_TESTATOR_MUTATION,
            variables: {
              id,
              testatorInputDetails,
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      redirectToNext() {
        if (this.wizard == 'trust' || this.wizard == 'children') {
          this.$router.push({ name: 'PeopleOverview', hash: '#children' });
        } else {
          this.$router.push('emergency-contacts');
        }
      },
      redirectBack() {
        if (this.wizard == 'trust') {
          this.$router.push({ name: 'PeopleOverview', hash: '#children' });
        } else {
          this.$router.push('guardian');
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .trust-termination-age {
    width: 280px;
  }
  .button-container {
    margin-top: 40px;
    display: flex;
    gap: 20px;
  }
</style>
